import api from '../lib/api';
import type {
  MediaAssetDto,
  MediaAssetListItemDto,
  MediaAssetUsageDto,
  MediaBindingDto,
  MediaSearchPage,
  MediaSearchParams,
  IngestRemoteUrlRequest,
  AttachBindingRequest,
  UpdateBindingRequest,
  UploadFileOptions,
  BatchUploadFileOptions,
  MediaBatchUploadResult,
  MediaTenantStatsDto,
  ReconcileBindingsRequest,
  MediaReconcileResult,
} from '../types/media';

const base = (tenantId: string) => `/api/v1/tenants/${tenantId}/media`;

/** Tenant-scoped media library (asset CRUD + bindings + dedup search). */
export const mediaApi = {
  /** Multipart upload — backend validates magic bytes, MIME, size, scope/role allowlist. */
  upload: (
    tenantId: string,
    file: File,
    opts: UploadFileOptions = {},
  ): Promise<MediaAssetDto> => {
    const form = new FormData();
    form.append('file', file);
    if (opts.scope) form.append('scope', opts.scope);
    if (opts.role) form.append('role', opts.role);
    if (opts.altText) form.append('altText', opts.altText);
    if (opts.attachOwnerId) form.append('attachOwnerId', opts.attachOwnerId);
    if (opts.attachSortOrder !== undefined) form.append('attachSortOrder', String(opts.attachSortOrder));
    if (opts.attachIsPrimary) form.append('attachIsPrimary', 'true');

    return api
      .post<MediaAssetDto>(`${base(tenantId)}/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: opts.signal,
        onUploadProgress: (e) => {
          if (!opts.onProgress) return;
          if (e.total && e.total > 0) {
            opts.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
          }
        },
      })
      .then((r) => r.data);
  },

  /** Batch upload — N dosya tek HTTP request. Per-file başarı/hata response içinde döner. */
  uploadBatch: (
    tenantId: string,
    files: File[],
    opts: BatchUploadFileOptions = {},
  ): Promise<MediaBatchUploadResult> => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    if (opts.scope) form.append('scope', opts.scope);
    if (opts.role) form.append('role', opts.role);
    if (opts.altText) form.append('altText', opts.altText);
    if (opts.attachOwnerId) form.append('attachOwnerId', opts.attachOwnerId);
    if (opts.attachStartSortOrder !== undefined) {
      form.append('attachStartSortOrder', String(opts.attachStartSortOrder));
    }
    return api
      .post<MediaBatchUploadResult>(`${base(tenantId)}/batch`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: opts.signal,
        onUploadProgress: (e) => {
          if (!opts.onProgress) return;
          if (e.total && e.total > 0) {
            opts.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
          }
        },
      })
      .then((r) => r.data);
  },

  /** Pull a remote URL through backend (Uploaded mode = downloads + stores; RemoteReference = SuperAdmin only). */
  ingestUrl: (tenantId: string, body: IngestRemoteUrlRequest, signal?: AbortSignal): Promise<MediaAssetDto> =>
    api.post<MediaAssetDto>(`${base(tenantId)}/from-url`, body, { signal }).then((r) => r.data),

  /** Search the asset library (paged). */
  search: (
    tenantId: string,
    params: MediaSearchParams = {},
    signal?: AbortSignal,
  ): Promise<MediaSearchPage> =>
    api
      .get<MediaSearchPage>(`${base(tenantId)}/`, {
        params,
        signal,
        // Axios default `?contentType[]=image/jpeg&contentType[]=image/png` brackets
        // formatında gönderir; ASP.NET Core minimal API ise `query["contentType"]`
        // (brackets'sız) okur — bu yüzden filter sessiz fail eder. `indexes: null`
        // ile `?contentType=image/jpeg&contentType=image/png` (repeat) formatına
        // çevrilir, StringValues doğru parse eder.
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data),

  /** Dedup lookup by sha256 hex — UI shows "this file already exists". */
  findBySha256: (
    tenantId: string,
    sha256: string,
    signal?: AbortSignal,
  ): Promise<MediaAssetListItemDto | null> =>
    api
      .get<MediaAssetListItemDto>(`${base(tenantId)}/by-sha256/${sha256}`, {
        signal,
        validateStatus: (s) => s === 200 || s === 404,
      })
      .then((r) => (r.status === 404 ? null : r.data)),

  /** Asset detail with fresh signed URL. */
  getAsset: (tenantId: string, assetId: string, signal?: AbortSignal): Promise<MediaAssetDto> =>
    api.get<MediaAssetDto>(`${base(tenantId)}/${assetId}`, { signal }).then((r) => r.data),

  /** Soft-delete asset (and all bindings). Returns 204 on success. */
  deleteAsset: (tenantId: string, assetId: string): Promise<void> =>
    api.delete(`${base(tenantId)}/${assetId}`).then(() => undefined),

  /** Attach an existing asset to an owner (Product, Brand, …). */
  attach: (
    tenantId: string,
    assetId: string,
    body: AttachBindingRequest,
  ): Promise<MediaBindingDto> =>
    api.post<MediaBindingDto>(`${base(tenantId)}/${assetId}/bindings`, body).then((r) => r.data),

  /** Detach a binding (asset stays, ref count goes down). */
  detach: (tenantId: string, bindingId: string): Promise<void> =>
    api.delete(`${base(tenantId)}/bindings/${bindingId}`).then(() => undefined),

  /** Patch sortOrder / isPrimary / altText / fileName on a binding. */
  updateBinding: (
    tenantId: string,
    bindingId: string,
    body: UpdateBindingRequest,
  ): Promise<MediaBindingDto> =>
    api
      .patch<MediaBindingDto>(`${base(tenantId)}/bindings/${bindingId}`, body)
      .then((r) => r.data),

  /** Asset'in kullanıldığı tüm yerler (MediaLibrary "kullanım listesi" dialog'u). */
  listAssetUsages: (
    tenantId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<MediaAssetUsageDto[]> =>
    api
      .get<MediaAssetUsageDto[]>(`${base(tenantId)}/${assetId}/usages`, { signal })
      .then((r) => r.data),

  /** All bindings on an owner (sorted, primary flagged, signed URLs batched). */
  listOwnerBindings: (
    tenantId: string,
    ownerScope: string,
    ownerId: string,
    signal?: AbortSignal,
  ): Promise<MediaBindingDto[]> =>
    api
      .get<MediaBindingDto[]>(`${base(tenantId)}/owners/${ownerScope}/${ownerId}`, { signal })
      .then((r) => r.data),

  /** Tenant-level usage / cost / 30-day growth — Faz 6 stats. */
  getTenantStats: (tenantId: string, signal?: AbortSignal): Promise<MediaTenantStatsDto> =>
    api
      .get<MediaTenantStatsDto>(`${base(tenantId)}/stats`, { signal })
      .then((r) => r.data),

  /**
   * tus.io chunked / resumable upload finalize. Bütün chunk'lar yüklendikten
   * sonra çağrılır; backend chunk file'ı open edip mevcut UploadAsync akışına
   * verir, MediaAsset üretir.
   */
  finalizeResumable: (tenantId: string, sessionId: string): Promise<MediaAssetDto> =>
    api
      .post<MediaAssetDto>(`${base(tenantId)}/upload/sessions/${sessionId}/finalize`)
      .then((r) => r.data),

  /** Kullanıcı iptali — backend chunk file'ı silip status=Aborted'a çeker. */
  abortResumable: (tenantId: string, sessionId: string): Promise<void> =>
    api.delete(`${base(tenantId)}/upload/sessions/${sessionId}`).then(() => undefined),

  /**
   * Reconcile description-media bindings for an owner (AB#1100).
   * Idempotent — backend computes attach/detach delta against `assetIds` order.
   * Empty array removes all bindings for the given (scope, owner, role) triple.
   */
  reconcile: (
    tenantId: string,
    body: ReconcileBindingsRequest,
    signal?: AbortSignal,
  ): Promise<MediaReconcileResult> =>
    api
      .post<MediaReconcileResult>(`${base(tenantId)}/reconcile`, body, { signal })
      .then((r) => r.data),
};

/** Human-readable byte formatter (1.2 MB / 540 KB style). Locale-agnostic units. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Chart-axis-friendly byte formatter: prefers GB so axis ticks stay compact on
 * stacked-bar / line charts. Falls back to MB below 1 GB to keep small datasets
 * readable. Use this for chart axis ticks where `formatBytes` produces uneven
 * unit jumps (B/KB/MB/GB) that look noisy on a Y-axis.
 */
export function formatGigabytes(bytes: number): string {
  if (bytes <= 0) return '0 GB';
  const gb = bytes / 1024 / 1024 / 1024;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / 1024 / 1024;
  return `${mb.toFixed(0)} MB`;
}

/** True for image/* assets that we can render in <img>. */
export function isImageContentType(contentType: string | null | undefined): boolean {
  return !!contentType && contentType.toLowerCase().startsWith('image/');
}
